import React, { useState, useEffect, useCallback, useMemo } from 'react';
import moment from 'moment';
import Cookies from 'js-cookie';

import 'moment/locale/ja';

moment.locale('ja');

const SHIFTS_API = 'https://system.sales-management.cloud/api/shifts.php';
const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

const isSameUserId = (a, b) => String(a) === String(b);

const hasShiftTime = (shift) => {
  const candidates = [
    shift?.start_time,
    shift?.end_time,
    shift?.shift_in,
    shift?.shift_out,
  ];

  return candidates.some((value) => value != null && String(value).trim() !== '');
};

function Shift() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarInfo, setCalendarInfo] = useState(null);
  const [users, setUsers] = useState([]);
  const [shifts, setShifts] = useState({});
  const [attendance, setAttendance] = useState({});
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'

  const today = moment().format('YYYY-MM-DD');

  // 認証データ取得
  const getAuthData = useCallback(() => {
    const token = Cookies.get('auth_token');
    const userInfo = Cookies.get('user_info');
    const user = userInfo ? JSON.parse(userInfo) : null;
    return { token, user };
  }, []);

  // シフトデータ取得
  const fetchShifts = useCallback(async (date = currentDate) => {
    setLoading(true);
    setError('');
    
    try {
      const { token, user } = getAuthData();
      
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      if (user) {
        setCurrentUserId(user.id);
        setCurrentUserRole(user.role);
      }

      const year = moment(date).format('YYYY');
      const month = moment(date).format('M');

      const response = await fetch(`${SHIFTS_API}?year=${year}&month=${month}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      

      if (data.status === 'success') {
        setCalendarInfo(data.calendar_info || null);
        setUsers(data.users || []);
        
        // 日付ごとのシフトと出勤データをマップに変換
        const shiftsMap = {};
        const attendanceMap = {};
        
        if (data.data) {
          Object.keys(data.data).forEach(dateKey => {
            const dayData = data.data[dateKey];
            shiftsMap[dateKey] = dayData.shifts || [];
            attendanceMap[dateKey] = dayData.attendance || [];
          });
        }
        
        setShifts(shiftsMap);
        setAttendance(attendanceMap);
      } else {
        setError(data.message || 'シフトの取得に失敗しました');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('通信エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }, [currentDate, getAuthData]);

  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  // シフト登録
  const handleAddShift = useCallback(async (date) => {
    const dateStr = moment(date).format('YYYY-MM-DD');

    if (moment(dateStr).isSameOrBefore(today, 'day')) {
      setError('今日より後の日付のみ登録できます');
      return;
    }

    setError('');
    setSuccess('');

    try {
      const { token } = getAuthData();
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const response = await fetch(SHIFTS_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ date: dateStr })
      });

      const data = await response.json();
      

      if (data.status === 'success') {
        setSuccess('シフトを登録しました');
        fetchShifts();
      } else {
        setError(data.message || 'シフトの登録に失敗しました');
      }
    } catch (err) {
      console.error('Add shift error:', err);
      setError('通信エラーが発生しました');
    }
  }, [today, getAuthData, fetchShifts]);

  // シフト削除
  const handleDeleteShift = useCallback(async (date) => {
    const dateStr = moment(date).format('YYYY-MM-DD');

    if (moment(dateStr).isSameOrBefore(today, 'day')) {
      setError('今日より後の日付のみ削除できます');
      return;
    }

    if (!window.confirm('このシフトを削除しますか?')) return;

    setError('');
    setSuccess('');

    try {
      const { token } = getAuthData();
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const response = await fetch(SHIFTS_API, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ date: dateStr })
      });

      const data = await response.json();
      

      if (data.status === 'success') {
        setSuccess('シフトを削除しました');
        fetchShifts();
      } else {
        setError(data.message || 'シフトの削除に失敗しました');
      }
    } catch (err) {
      console.error('Delete shift error:', err);
      setError('通信エラーが発生しました');
    }
  }, [today, getAuthData, fetchShifts]);

  // カレンダー日付生成
  const generateCalendarDays = useCallback(() => {
    const start = calendarInfo 
      ? moment(calendarInfo.start_date) 
      : moment(currentDate).startOf('month');
    const end = calendarInfo 
      ? moment(calendarInfo.end_date) 
      : moment(currentDate).endOf('month');
    
    const days = [];

    // 月の開始曜日まで空白セルを追加（PC用）
    for (let i = 0; i < start.day(); i++) {
      days.push(null);
    }

    // 日付を追加
    let current = start.clone();
    while (current.isSameOrBefore(end)) {
      days.push(current.clone());
      current.add(1, 'day');
    }

    return days;
  }, [calendarInfo, currentDate]);

  // 月移動
  const navigateMonth = useCallback((offset) => {
    const newDate = moment(currentDate).add(offset, 'month').toDate();
    setCurrentDate(newDate);
    fetchShifts(newDate);
  }, [currentDate, fetchShifts]);

  const handlePrevMonth = () => navigateMonth(-1);
  const handleNextMonth = () => navigateMonth(1);
  const handleToday = useCallback(() => {
    const newDate = new Date();
    setCurrentDate(newDate);
    fetchShifts(newDate);
  }, [fetchShifts]);

  // ユーザーステータス取得
  const getUserStatus = useCallback((date, userId) => {
    const dateStr = moment(date).format('YYYY-MM-DD');
    
    if (moment(dateStr).isSameOrAfter(today, 'day')) {
      // 今日以降: シフト時刻データ（登録あり=出勤予定）
      const dayShifts = shifts[dateStr] || [];
      return dayShifts.some((shift) => isSameUserId(shift?.user_id, userId) && hasShiftTime(shift));
    } else {
      // 昨日以前: 出勤データ
      const dayAttendance = attendance[dateStr] || [];
      const userAttendance = dayAttendance.find((att) => isSameUserId(att?.user_id, userId));
      return userAttendance ? userAttendance.status : null;
    }
  }, [today, shifts, attendance]);

  const calendarDays = generateCalendarDays();

  // 対象月で1件もシフトがないユーザーは非表示
  const visibleUsers = useMemo(() => {
    const userIdsWithShift = new Set();
    const currentMonth = moment(currentDate).format('YYYY-MM');

    Object.entries(shifts).forEach(([dateKey, dayShifts]) => {
      if (!dateKey.startsWith(`${currentMonth}-`)) {
        return;
      }

      (dayShifts || []).forEach((shift) => {
        if (shift?.user_id != null && hasShiftTime(shift)) {
          userIdsWithShift.add(String(shift.user_id));
        }
      });
    });

    return users.filter((user) => userIdsWithShift.has(String(user.user_id)));
  }, [users, shifts, currentDate]);

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <Header
        currentDate={currentDate}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
        onToday={handleToday}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      <MessageDisplay error={error} success={success} />

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <CalendarGrid
            calendarDays={calendarDays}
            users={visibleUsers}
            today={today}
            currentUserId={currentUserId}
            currentUserRole={currentUserRole}
            getUserStatus={getUserStatus}
            onAddShift={handleAddShift}
            onDeleteShift={handleDeleteShift}
            currentDate={currentDate}
            viewMode={viewMode}
          />
          <Legend />
        </>
      )}
    </div>
  );
}

// ヘッダー
const Header = ({ currentDate, onPrevMonth, onNextMonth, onToday, viewMode, onViewModeChange }) => (
  <div className="mb-6">
    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">シフト管理</h1>
    
    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 mb-4">
      <div className="flex items-center gap-2">
        <button
          onClick={onPrevMonth}
          className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm"
        >
          前月
        </button>
        <button
          onClick={onToday}
          className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
        >
          今月
        </button>
        <button
          onClick={onNextMonth}
          className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm"
        >
          次月
        </button>
        {/* SP: 表示形式切り替えボタン */}
        <div className="md:hidden flex items-center gap-1 border border-gray-300 rounded-md p-1">
          <button
            onClick={() => onViewModeChange('list')}
            className={`px-2 py-1 rounded text-xs transition-colors ${
              viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            リスト
          </button>
          <button
            onClick={() => onViewModeChange('calendar')}
            className={`px-2 py-1 rounded text-xs transition-colors ${
              viewMode === 'calendar' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            カレンダー
          </button>
        </div>
      </div>
      
      <h2 className="text-lg md:text-xl font-semibold text-gray-800">
        {moment(currentDate).format('YYYY年M月')}
      </h2>
    </div>
  </div>
);

// メッセージ表示
const MessageDisplay = ({ error, success }) => (
  <>
    {error && (
      <div className="mb-4 p-3 md:p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-xs md:text-sm text-red-600">{error}</p>
      </div>
    )}
    {success && (
      <div className="mb-4 p-3 md:p-4 bg-green-50 border border-green-200 rounded-md">
        <p className="text-xs md:text-sm text-green-600">{success}</p>
      </div>
    )}
  </>
);

// ローディング
const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-20 text-gray-500">
    読み込み中...
  </div>
);

// カレンダーグリッドコンポーネント
const CalendarGrid = ({ calendarDays, users, today, currentUserId, currentUserRole, getUserStatus, onAddShift, onDeleteShift, currentDate, viewMode }) => {
  // SP用: 現在の月のみを抽出（1日～末日）
  const currentMonth = moment(currentDate).format('YYYY-MM');
  const daysCurrentMonthOnly = calendarDays.filter(day => day !== null && day.format('YYYY-MM') === currentMonth);

  return (
    <div className="rounded-lg shadow-sm md:shadow-md overflow-hidden">
      <WeekdayHeader viewMode={viewMode} />
      {/* SP list: 1日～末日のみ縦並び */}
      {viewMode === 'list' && (
        <div className="flex flex-col md:hidden bg-gray-100">
          {daysCurrentMonthOnly.map((day) => (
            <CalendarDay
              key={day.format('YYYY-MM-DD')}
              day={day}
              users={users}
              today={today}
              currentUserId={currentUserId}
              currentUserRole={currentUserRole}
              getUserStatus={getUserStatus}
              onAddShift={onAddShift}
              onDeleteShift={onDeleteShift}
              viewMode={viewMode}
            />
          ))}
        </div>
      )}
      {/* PC or SP calendar: グリッド表示（空白セルあり） */}
      <div className={`${viewMode === 'list' ? 'hidden md:grid' : 'grid'} grid-cols-7 gap-px bg-gray-100`}>
        {calendarDays.map((day, index) => (
          <CalendarDay
            key={day ? day.format('YYYY-MM-DD') : `empty-${index}`}
            day={day}
            users={users}
            today={today}
            currentUserId={currentUserId}
            currentUserRole={currentUserRole}
            getUserStatus={getUserStatus}
            onAddShift={onAddShift}
            onDeleteShift={onDeleteShift}
            viewMode={viewMode}
          />
        ))}
      </div>
    </div>
  );
};

// 曜日ヘッダー（カレンダー表示時のみ）
const WeekdayHeader = ({ viewMode }) => (
  <div className={`${viewMode === 'list' ? 'hidden md:grid' : 'grid'} grid-cols-7 bg-gray-50`}>
    {WEEKDAYS.map((day, index) => (
      <div
        key={index}
        className={`p-3 text-center text-sm font-semibold ${
          index === 0 ? 'text-red-600' : index === 6 ? 'text-blue-600' : 'text-gray-700'
        }`}
      >
        {day}
      </div>
    ))}
  </div>
);

// カレンダー日付セル
const CalendarDay = ({ day, users, today, currentUserId, currentUserRole, getUserStatus, onAddShift, onDeleteShift, viewMode }) => {
  // PC のみ null（空白セル）を表示
  if (!day) {
    return <div className="md:min-h-32" />;
  }

  const dateStr = day.format('YYYY-MM-DD');
  const isToday = dateStr === today;
  const isPast = moment(dateStr).isBefore(today, 'day');

  // viewMode が 'calendar' の場合は PC と同じスタイル、'list' の場合は SP スタイル
  const isCalendarMode = viewMode === 'calendar';

  return (
    <div
      className={`${
        isCalendarMode 
          ? 'p-2 md:p-2 md:min-h-32 rounded-none' 
          : 'p-3 md:p-2 md:min-h-32 rounded-lg md:rounded-none'
      } ${
        isToday ? 'bg-blue-50' : isPast ? 'bg-gray-50' : 'bg-white'
      } transition-colors md:hover:bg-gray-50`}
    >
      <DayNumber day={day} isToday={isToday} />
      <UserList
        users={users}
        day={day}
        isPast={isPast}
        currentUserId={currentUserId}
        currentUserRole={currentUserRole}
        getUserStatus={getUserStatus}
        onAddShift={onAddShift}
        onDeleteShift={onDeleteShift}
        viewMode={viewMode}
      />
    </div>
  );
};

// 日付番号（視認性向上）
const DayNumber = ({ day, isToday }) => (
  <div className="flex items-baseline gap-1 mb-2">
    <span className={`text-base md:text-sm font-semibold ${
      isToday ? 'text-blue-700' :
      day.day() === 0 ? 'text-red-600' :
      day.day() === 6 ? 'text-blue-600' :
      'text-gray-800'
    }`}>
      {day.format('D')}
    </span>
    <span className="text-[10px] md:text-xs text-gray-400">{WEEKDAYS[day.day()]}</span>
  </div>
);

// ユーザーリスト
const UserList = ({ users, day, isPast, currentUserId, currentUserRole, getUserStatus, onAddShift, onDeleteShift, viewMode }) => {
  // 状態サマリー（SP表示用）
  const statuses = users.map(u => ({ id: u.user_id, s: getUserStatus(day, u.user_id) }));
  const otherStatuses = statuses.filter(v => v.id !== currentUserId).map(v => v.s);
  const futurePresentCount = !isPast ? otherStatuses.filter(Boolean).length : null;
  const futureAbsentCount = !isPast ? (otherStatuses.length - futurePresentCount) : null;
  const pastPresent = isPast ? otherStatuses.filter(s => s === 'present').length : null;
  const pastAbsent = isPast ? otherStatuses.filter(s => s === 'absent').length : null;

  const currentUser = users.find(u => u.user_id === currentUserId);
  const currentStatus = currentUser ? getUserStatus(day, currentUser.user_id) : null;

  // viewMode が 'calendar' の場合は常に PC 表示（横並び）
  const isListMode = viewMode === 'list';

  return (
    <div className="space-y-2">
      {/* SP リスト表示: 自分の状態 → 他ユーザーサマリー */}
      {isListMode && (
        <div className="md:hidden space-y-2">
          {currentUser && (
            <UserItem
              key={currentUser.user_id}
              user={currentUser}
              day={day}
              isPast={isPast}
              isCurrentUser={true}
              currentUserRole={currentUserRole}
              status={currentStatus}
              onAddShift={onAddShift}
              onDeleteShift={onDeleteShift}
            />
          )}
          <div className="flex items-center justify-between text-[11px] text-gray-600">
            {isPast ? (
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-600"></span>出勤 {pastPresent}</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-600"></span>欠勤 {pastAbsent}</span>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-600"></span>出勤予定 {futurePresentCount}</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-600"></span>欠勤予定 {futureAbsentCount}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* PC・カレンダー表示: 横並びで全ユーザー表示（補足は hover） */}
      <div className={`${isListMode ? 'hidden md:flex' : 'flex'} flex-wrap items-center gap-x-2 gap-y-1`}>
        {users.map(user => (
          <UserItem
            key={user.user_id}
            user={user}
            day={day}
            isPast={isPast}
            isCurrentUser={user.user_id === currentUserId}
            currentUserRole={user.user_id === currentUserId ? currentUserRole : null}
            status={getUserStatus(day, user.user_id)}
            onAddShift={onAddShift}
            onDeleteShift={onDeleteShift}
          />
        ))}
      </div>
    </div>
  );
};

// ユーザーアイテム
const UserItem = ({ user, day, isPast, isCurrentUser, currentUserRole, status, onAddShift, onDeleteShift }) => {
  if (isPast) {
    return <PastUserStatus user={user} status={status} />;
  }

  if (isCurrentUser) {
    return (
      <CurrentUserShift
        user={user}
        day={day}
        hasShift={status}
        currentUserRole={currentUserRole}
        onAddShift={onAddShift}
        onDeleteShift={onDeleteShift}
      />
    );
  }

  return <OtherUserShift user={user} hasShift={status} />;
};

// 過去の出勤状況（色は状態のみ、補足は hover）
const PastUserStatus = ({ user, status }) => (
  <div className="group inline-flex items-center gap-1 text-[10px] md:text-xs">
    <span className="text-xs md:text-sm">{user.emoji || '👤'}</span>
    <span className={`w-2 h-2 rounded-full ${
      status === 'present' ? 'bg-green-600' : status === 'absent' ? 'bg-red-600' : 'bg-gray-300'
    }`}></span>
    <span className="hidden md:inline text-gray-500 group-hover:inline">
      {status === 'present' ? '出勤' : status === 'absent' ? '欠勤' : '不明'}
    </span>
  </div>
);

// 自分のシフト（SP はタップ領域を確保、PC は hover 補足）
const CurrentUserShift = ({ user, day, hasShift, currentUserRole, onAddShift, onDeleteShift }) => {
  // 管理者以外は出勤/欠勤表示
  const isAdmin = currentUserRole === 'admin' || currentUserRole === 'superadmin';
  
  if (isAdmin) {
    // 管理者の場合は従来通りの表示
    if (hasShift) {
      return (
        <button
          onClick={() => onDeleteShift(day)}
          className="group inline-flex items-center gap-1 text-[11px] md:text-xs text-green-700 hover:text-red-600 transition-colors px-2 py-2 md:py-1 rounded-md bg-green-50 md:bg-transparent"
          title="シフト削除"
        >
          <span className="text-sm md:text-sm">{user.emoji || '👤'}</span>
          <span className="font-medium">✓</span>
          <span className="hidden md:inline text-gray-500 group-hover:inline">登録済み（クリックで削除）</span>
        </button>
      );
    }
    return (
      <button
        onClick={() => onAddShift(day)}
        className="group inline-flex items-center gap-1 text-[11px] md:text-xs text-blue-700 transition-colors px-2 py-2 md:py-1 rounded-md bg-blue-50 md:bg-transparent hover:text-blue-800"
        title="シフト登録"
      >
        <span className="text-sm md:text-sm">{user.emoji || '👤'}</span>
        <span className="font-medium">+</span>
        <span className="hidden md:inline text-gray-500 group-hover:inline">クリックで登録</span>
      </button>
    );
  }
  
  // 一般ユーザーの場合は出勤/欠勤表示（シフト時刻あり=出勤予定）
  if (hasShift) {
    // シフト時刻あり = 出勤予定
    return (
      <button
        onClick={() => onDeleteShift(day)}
        className="group inline-flex items-center gap-1 text-[11px] md:text-xs text-green-600 hover:text-red-600 transition-colors px-2 py-2 md:py-1 rounded-md bg-green-50 md:bg-transparent"
        title="欠勤に変更"
      >
        <span className="text-sm md:text-sm">{user.emoji || '👤'}</span>
        <span className="font-medium">出勤</span>
        <span className="hidden md:inline text-gray-500 group-hover:inline">クリックで欠勤に変更</span>
      </button>
    );
  }
  
  // シフト時刻なし = 欠勤予定
  return (
    <button
      onClick={() => onAddShift(day)}
      className="group inline-flex items-center gap-1 text-[11px] md:text-xs text-red-600 hover:text-green-600 transition-colors px-2 py-2 md:py-1 rounded-md bg-red-50 md:bg-transparent"
      title="出勤に変更"
    >
      <span className="text-sm md:text-sm">{user.emoji || '👤'}</span>
      <span className="font-medium">欠勤</span>
      <span className="hidden md:inline text-gray-500 group-hover:inline">クリックで出勤に変更</span>
    </button>
  );
};

// 他ユーザーのシフト（PC は横並び、補足は hover）
const OtherUserShift = ({ user, hasShift }) => (
  <div className="group inline-flex items-center gap-1 text-[10px] md:text-xs">
    <span className="text-xs md:text-sm">{user.emoji || '👤'}</span>
    <span className={`w-2 h-2 rounded-full ${hasShift ? 'bg-green-600' : 'bg-red-600'}`}></span>
    <span className="hidden md:inline text-gray-500 group-hover:inline">{hasShift ? '出勤予定' : '欠勤予定'}</span>
  </div>
);

// 凡例（色は状態のみ）
const Legend = () => (
  <div className="mt-4 flex flex-wrap gap-4 md:gap-6 text-xs md:text-sm text-gray-600">
    <div className="flex items-center gap-2">
      <span className="w-2.5 h-2.5 rounded-full bg-blue-600"></span>
      <span>自分の操作（登録/削除）</span>
    </div>
    <div className="flex items-center gap-2">
      <span className="w-2.5 h-2.5 rounded-full bg-green-600"></span>
      <span>過去: 出勤 / 未来: 出勤予定（デフォルト）</span>
    </div>
    <div className="flex items-center gap-2">
      <span className="w-2.5 h-2.5 rounded-full bg-red-600"></span>
      <span>過去: 欠勤 / 未来: 欠勤予定</span>
    </div>
    <div className="flex items-center gap-2">
      <span className="w-2.5 h-2.5 rounded-full bg-gray-300"></span>
      <span>情報なし</span>
    </div>
  </div>
);

export default Shift;